import {
  Component,
  Host,
  State,
  Prop,
  Element,
  h,
  getAssetPath,
  JSX
} from "@stencil/core";

import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";
import { ActionMenuModel, TabModel } from "@genexus/chameleon-controls-library";
import { TabItemModel } from "@genexus/chameleon-controls-library/dist/types/components/tab/types";

const USER_ICON = getIconPath({
  category: "system",
  name: "user",
  colorType: "on-surface"
});
const CHEVRON_DOWN_ICON = getIconPath({
  category: "system",
  name: "chevron-down",
  colorType: "on-primary"
});
const CHECK_CIRCLE_ICON = getIconPath({
  category: "system",
  name: "check-circle",
  colorType: "success"
});

// plugin icon if no iconUri was provided
const EXTENSION_ICON_IMG = getAssetPath(
  `./gx-ide-assets/plugin-manager/images/plugin-icon.svg`
);

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "chameleon/scrollbar",
  "components/button",
  "components/dropdown",
  "components/edit",
  "components/icon",
  "components/list-box",
  "components/tab",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];

@Component({
  tag: "gx-ide-plugin-details",
  styleUrl: "plugin-details.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/plugin-details"]
})
export class GxIdePluginDetails {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #chMarkdownViewer: HTMLChMarkdownViewerElement;

  // A map between docSection "id", and docSection "description".
  // "id" is generated inside this component.
  #docSectionIdDescriptionMap: Map<string, string> = new Map();

  @Element() el: HTMLGxIdePluginDetailsElement;

  @State() docsTabModel: TabModel = [];
  @State() selectedTabId: string;

  /**
   * Specifies all the information related to a plugin.
   */
  @Prop() readonly data!: PluginDetailsData;

  /**
   * Callback that should be invoked when the user performs the install or update action.
   * Receives an optional version string when a specific version is selected.
   */
  @Prop() readonly installCallback!: (version?: string) => Promise<void>;

  /**
   * Callback that should be invoked when the user attempts to open any link displayed in this component.
   */
  @Prop() readonly openLinkCallback!: (url: string) => Promise<void>;

  /**
   * Callback that should be invoked when the user performs the uninstall action.
   */
  @Prop() readonly uninstallCallback!: () => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    if (this.data.docSections?.length) {
      this.docsTabModel = this.data.docSections.map((docSection, i) => {
        const formatedId = this.#returnFormatedId(docSection.name);

        this.#docSectionIdDescriptionMap.set(formatedId, docSection.content);

        if (i === 0) {
          this.selectedTabId = formatedId;
        }
        return {
          id: formatedId,
          name: docSection.name
        } as TabItemModel;
      });
    }
  }

  componentDidLoad() {
    this.#chMarkdownViewer.addEventListener("click", event => {
      // Listens for clicks on links inside <ch-markdown-viewer>.
      // If a link (<a>) is clicked and openLinkCallback is defined,
      // it prevents the default navigation and delegates the event
      // to this.#openLinkHandler, passing the link's href.
      const path = event.composedPath() as EventTarget[];

      const link = path.find(el => el instanceof HTMLAnchorElement) as
        | HTMLAnchorElement
        | undefined;

      if (link?.href) {
        this.#openLinkHandler(link.href)(event);
      }
    });
  }

  #versionsMenuOptionsModel = (): ActionMenuModel => {
    const versionsToUpdateMenusModel: ActionMenuModel = this.data.versions?.map(
      version => {
        return { id: version, caption: version };
      }
    );
    return versionsToUpdateMenusModel;
  };

  #menuVersionSelectedHandler = (
    event: CustomEvent<{
      id?: string;
    }>
  ) => {
    const selectedVersion = event.detail.id;
    this.installCallback(selectedVersion);
  };

  #renderUninstallButton = (): JSX.Element => {
    return (
      <button class="button-tertiary" onClick={this.#uninstallClickedHandler}>
        {this.#componentLocale.actions.uninstall}
      </button>
    );
  };

  #installClickedHandler = () => {
    this.installCallback();
  };

  #updateVersionHandler = (id: string) => () => {
    this.installCallback(id);
  };

  #openLinkHandler = (link: string) => (event: MouseEvent) => {
    if (this.openLinkCallback) {
      event.preventDefault();
      this.openLinkCallback(link);
    }
  };

  #uninstallClickedHandler = () => {
    this.uninstallCallback();
  };

  #renderActionButtons = (): JSX.Element => {
    let actionButtonCaption;
    let actionButtonClasses = "button-primary action-button";
    let actionButtonCallback;
    const moreVersionsToUpdate = this.data.versions?.length;

    if (!this.data.installed) {
      actionButtonCaption = this.#componentLocale.actions.install;
      actionButtonCallback = this.#installClickedHandler;
    } else if (this.data.updateVersion) {
      actionButtonCaption = `${this.#componentLocale.actions.updateTo} ${
        this.data.updateVersion
      }`;
      actionButtonCallback = this.#updateVersionHandler(
        this.data.updateVersion
      );
    } else {
      actionButtonCaption = this.#componentLocale.actions.installed;
    }

    if (moreVersionsToUpdate) {
      actionButtonClasses = `${actionButtonClasses} more-versions-available`;
    }

    return [
      <div class="version-buttons-container">
        <button class={actionButtonClasses} onClick={actionButtonCallback}>
          {actionButtonCaption}
        </button>
        {moreVersionsToUpdate ? (
          <ch-action-menu-render
            class="dropdown button-primary button-icon-only"
            blockAlign="outside-end"
            inlineAlign="inside-start"
            model={this.#versionsMenuOptionsModel()}
            onButtonClick={this.#menuVersionSelectedHandler}
          >
            <ch-image src={CHEVRON_DOWN_ICON} class="icon-m"></ch-image>
          </ch-action-menu-render>
        ) : null}
      </div>
    ];
  };

  #tabSelectionChangedHandler = (
    event: CustomEvent<{
      lastSelectedIndex: number;
      newSelectedId: string;
      newSelectedIndex: number;
    }>
  ): void => {
    this.selectedTabId = event.detail.newSelectedId;
  };

  #renderDocSectionsSlots = (): JSX.Element => {
    return this.docsTabModel.map(docSectionTab => {
      if (docSectionTab.id === this.selectedTabId) {
        const markdownValue = this.#docSectionIdDescriptionMap.get(
          docSectionTab.id
        );

        return (
          <ch-markdown-viewer
            ref={(el: HTMLChMarkdownViewerElement) =>
              (this.#chMarkdownViewer = el as HTMLChMarkdownViewerElement)
            }
            slot={docSectionTab.id}
            key={docSectionTab.id}
            class="markdown spacing-body scrollable"
            theme="components/markdown-viewer"
            value={markdownValue}
          ></ch-markdown-viewer>
        );
      }
    });
  };

  #returnFormatedId = (rawId: string): string => {
    return rawId.toLowerCase().replace(/\s+/g, "-");
  };

  #renderLinkTag = (
    href: string,
    label: string,
    cssClass?: string,
    chImageSrc?: string
  ): HTMLAnchorElement => {
    return (
      <a
        href={href}
        target="_blank"
        onClick={this.#openLinkHandler(href)}
        class={cssClass}
      >
        {chImageSrc && <ch-image src={chImageSrc} class="icon-m"></ch-image>}
        {label}
      </a>
    );
  };

  #renderAsideContent = (): JSX.Element => {
    return this.data.infoCategories.map(infoCategory => {
      return (
        <div class="plugin__info-category">
          <h3 class="subtitle-semi-bold-s">{infoCategory.name}</h3>
          <ul
            class={{
              "fields__list": true,
              "fields__list--as-tags": infoCategory.displayAsTags
            }}
          >
            {infoCategory.fields.map(field => {
              // If the field name isn’t specified, only the value is shown — usually a link.
              const fieldValue: string | HTMLAnchorElement =
                typeof field.value === "string"
                  ? (field.value as string)
                  : this.#renderLinkTag(
                      (field.value as LinkData).url,
                      (field.value as LinkData).text,
                      "field__link"
                    );
              return (
                <li class="field__li">
                  {field.name && !infoCategory.displayAsTags
                    ? // displaysAsTags forces the items to be displayed as tags
                      // no span.field__name and span.field__value
                      [
                        <span class="field__name body-semi-bold-s">
                          {field.name}
                        </span>,
                        <span class="field__value body-regular-s">
                          {fieldValue}
                        </span>
                      ]
                    : fieldValue}
                </li>
              );
            })}
          </ul>
        </div>
      );
    });
  };

  render() {
    const renderActionButtons =
      !this.data.installed ||
      (this.data.installed && !!this.data.updateVersion);

    const titleLinkDataText = (this.data.name as LinkData).text;
    const titleLinkDataUrl = (this.data.name as LinkData).url;
    const titleHasLink = titleLinkDataText && titleLinkDataUrl;
    const titleText = titleLinkDataText || this.data.name;

    const publisherDataText = (this.data.publisher as LinkData).text;
    const publisherDataUrl = (this.data.publisher as LinkData).url;
    const publisherHasLink = publisherDataText && publisherDataUrl;
    const publisherText = publisherDataText || this.data.publisher;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          <header class="header">
            <img
              class="plugin__icon"
              src={this.data.iconUrl || EXTENSION_ICON_IMG}
              alt="plugin extension icon"
            />
            <div class="plugin__details">
              {titleHasLink ? (
                <h2 class="heading-5 has-link">
                  {this.#renderLinkTag(
                    titleLinkDataUrl,
                    titleText as string,
                    "text-underline"
                  )}
                </h2>
              ) : (
                <h2 class="heading-5">{titleText}</h2>
              )}
              {publisherHasLink ? (
                this.#renderLinkTag(
                  publisherDataUrl,
                  titleText as string,
                  "text-underline plugin__publisher caption-semi-bold-l",
                  USER_ICON
                )
              ) : (
                <p class="plugin__publisher caption-semi-bold-l">
                  <ch-image src={USER_ICON} class="icon-s"></ch-image>
                  {publisherText}
                </p>
              )}

              <p class="plugin__description">{this.data.description}</p>
              <div class="buttons-spacer plugin__actions-container">
                {renderActionButtons ? (
                  this.#renderActionButtons()
                ) : (
                  // installed and no updates
                  <p class="installed-container subtitle-semi-bold-s">
                    <ch-image src={CHECK_CIRCLE_ICON} class="icon-s"></ch-image>
                    {this.#componentLocale.actions.installed}
                  </p>
                )}
                {this.data.installed && this.#renderUninstallButton()}
              </div>
            </div>
          </header>
          <div class="main">
            {this.docsTabModel.length > 0 && (
              <ch-tab-render
                class="tab docsTab scrollable"
                tabListPosition="block-start"
                overflow="auto"
                contain="size"
                model={this.docsTabModel}
                onSelectedItemChange={this.#tabSelectionChangedHandler}
                selectedId={this.selectedTabId}
              >
                {this.#renderDocSectionsSlots()}
              </ch-tab-render>
            )}
          </div>
          <div class="aside spacing-body scrollable">
            {this.#renderAsideContent()}
          </div>
        </section>
        {/* <slot></slot> */}
      </Host>
    );
  }
}

// plugin-details.types.ts

export type LinkData = {
  text: string;
  url: string;
};

export type PluginFieldData = {
  name?: string;
  value: string | LinkData;
};

export type PluginInfoCategoryData = {
  name: string;
  fields: PluginFieldData[];
  displayAsTags: boolean;
};

export type DocSectionData = {
  name: string;
  content: string;
};

export type PluginDetailsData = {
  id: string;
  name: string | LinkData;
  publisher: string | LinkData;
  installed: boolean;
  updateVersion?: string;
  versions: string[];
  iconUrl: string;
  description: string;
  docSections: DocSectionData[];
  infoCategories: PluginInfoCategoryData[];
};
